import * as XLSX from 'xlsx-js-style';

/**
 * Bienestar Corporativo (admin) — export a Excel del tablero: hojas Resumen, Heatmap,
 * Participación y Adopción. Las celdas protegidas por k-anonymity salen como "Protegido" (la
 * protección viene del backend; aquí solo se respeta) y la hoja de adopción es por
 * departamento, NUNCA por persona.
 */

const DIMENSION_LABELS = {
  energy: 'Energía',
  stress: 'Estrés',
  rest: 'Descanso',
  motivation: 'Motivación'
};

const headerStyle = {
  font: { bold: true, color: { rgb: 'FFFFFF' } },
  fill: { fgColor: { rgb: '1A1A1A' } },
  alignment: { horizontal: 'center' }
};

const styleHeaderRow = (sheet, columns) => {
  for (let c = 0; c < columns; c += 1) {
    const address = XLSX.utils.encode_cell({ r: 0, c });
    if (sheet[address]) sheet[address].s = headerStyle;
  }
};

const cellValue = (score, isProtected) => (isProtected || score === null || score === undefined ? 'Protegido' : score);

export const exportWellbeingToExcel = ({ organizationName, summary, heatmap, participation, departmentUsage }) => {
  const workbook = XLSX.utils.book_new();

  // Hoja 1: Resumen
  const summaryRows = [
    ['Organización', organizationName],
    ['Índice OTRIX de Bienestar (0-100)', cellValue(summary?.score, summary?.protected)],
    ['Personas que respondieron', summary?.protected ? 'Protegido' : summary?.respondents ?? 0],
    ['Miembros', summary?.members ?? 0],
    ['Participación (%)', summary?.protected ? 'Protegido' : summary?.participation_percent ?? 0],
    [],
    ['Dimensión', 'Puntaje']
  ];
  (summary?.dimensions || []).forEach((dimension) => {
    summaryRows.push([DIMENSION_LABELS[dimension.dimension] || dimension.dimension, cellValue(dimension.score, dimension.protected)]);
  });
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryRows);
  summarySheet['!cols'] = [{ wch: 34 }, { wch: 18 }];
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Resumen');

  // Hoja 2: Heatmap por departamento
  const heatmapRows = [
    ['Departamento', 'Miembros', 'Participación (%)', 'General', 'Energía', 'Estrés', 'Descanso', 'Motivación']
  ];
  (heatmap || []).forEach((row) => {
    heatmapRows.push([
      row.department_name || 'Sin departamento',
      row.members,
      row.protected || row.participation_percent === null ? 'Protegido' : row.participation_percent,
      cellValue(row.overall, row.protected),
      ...(row.cells || []).map((cell) => cellValue(cell.score, cell.protected))
    ]);
  });
  const heatmapSheet = XLSX.utils.aoa_to_sheet(heatmapRows);
  heatmapSheet['!cols'] = [{ wch: 26 }, { wch: 10 }, { wch: 16 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 12 }];
  styleHeaderRow(heatmapSheet, heatmapRows[0].length);
  XLSX.utils.book_append_sheet(workbook, heatmapSheet, 'Heatmap');

  // Hoja 3: Participación por semana
  const participationRows = [['Semana', 'Respondieron', 'Miembros', '%']];
  (participation || []).forEach((point) => {
    participationRows.push([point.week_key, point.respondents, point.members, point.percent]);
  });
  const participationSheet = XLSX.utils.aoa_to_sheet(participationRows);
  participationSheet['!cols'] = [{ wch: 12 }, { wch: 14 }, { wch: 10 }, { wch: 8 }];
  styleHeaderRow(participationSheet, participationRows[0].length);
  XLSX.utils.book_append_sheet(workbook, participationSheet, 'Participación');

  // Hoja 4: Adopción de la app por departamento. Es uso, no bienestar, y por eso puede ir sin
  // proteger; lo que NO puede ir es una persona: aquí solo entran filas agregadas por área.
  if (departmentUsage && departmentUsage.length > 0) {
    const adoptionRows = [
      [
        'Departamento',
        'Miembros',
        'Activos (30 días)',
        'Adopción (%)',
        'Activos (7 días)',
        'Adopción semanal (%)',
        'Entrenamientos',
        'Minutos',
        'Entrenamientos por persona',
        'Cumplimiento promedio (%)',
        'Semanas'
      ]
    ];
    departmentUsage.forEach((row) => {
      adoptionRows.push([
        row.department_name || 'Sin departamento',
        row.members,
        row.active_members,
        row.adoption_percent,
        row.active_last_week,
        row.weekly_adoption_percent,
        row.workouts,
        row.minutes,
        row.workouts_per_member,
        row.avg_completion_percent === null || row.avg_completion_percent === undefined ? 'Sin datos' : row.avg_completion_percent,
        row.weeks
      ]);
    });
    const adoptionSheet = XLSX.utils.aoa_to_sheet(adoptionRows);
    adoptionSheet['!cols'] = [
      { wch: 26 }, { wch: 10 }, { wch: 16 }, { wch: 13 }, { wch: 15 },
      { wch: 20 }, { wch: 15 }, { wch: 10 }, { wch: 24 }, { wch: 22 }, { wch: 9 }
    ];
    styleHeaderRow(adoptionSheet, adoptionRows[0].length);
    XLSX.utils.book_append_sheet(workbook, adoptionSheet, 'Adopción');
  }

  const safeName = (organizationName || 'organizacion').replace(/[^\w\sÁÉÍÓÚÑáéíóúñ-]/g, '').trim().replace(/\s+/g, '-');
  XLSX.writeFile(workbook, `bienestar-${safeName}.xlsx`);
};

export default exportWellbeingToExcel;
